import { useEffect, useState } from 'react'
import { Network } from 'lucide-react'
import { apiGet, apiPost } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Skeleton,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

export default function AdminSSHNetworksPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()
  const [networks, setNetworks] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newNetwork, setNewNetwork] = useState('')
  const [saving, setSaving] = useState(false)

  const fetchNetworks = async () => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    try {
      const data = await apiGet<{ networks: string[] }>(
        `/api/v1/tenants/${tenantId}/ssh-networks`,
      )
      setNetworks(data.networks ?? [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchNetworks() }, [tenantId])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const network = newNetwork.trim()
    if (!network) return
    setSaving(true)
    setError(null)
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/ssh-networks`, { network })
      setNewNetwork('')
      toast({ title: 'Network added', variant: 'success' })
      fetchNetworks()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Add failed', description: e.message, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (network: string) => {
    if (!confirm(`Remove ${network}? Users who already ran "idefy ssh config install" will keep the old entry in their ~/.ssh/config until they rerun it.`)) return
    setError(null)
    try {
      // DELETE with JSON body — keep raw fetch (apiDelete body support may vary by caller)
      const res = await fetch(`/api/v1/tenants/${tenantId}/ssh-networks`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ network }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast({ title: 'Network removed', variant: 'success' })
      fetchNetworks()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Delete failed', description: e.message, variant: 'danger' })
    }
  }

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="SSH Native-Access Networks"
        description="Define the private network ranges routed through the Idefy bastion for native SSH access."
      />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      <Card className="mb-6 p-5">
        <form onSubmit={handleAdd} className="flex items-end gap-3">
          <FormField
            label="Network (CIDR or hostname pattern)"
            htmlFor="ssh-network"
            className="flex-1"
            hint="Only add your organization's own private network ranges."
          >
            <Input
              id="ssh-network"
              value={newNetwork}
              onChange={(e) => setNewNetwork(e.target.value)}
              placeholder="10.2.0.0/16"
            />
          </FormField>
          <Button type="submit" disabled={saving || !newNetwork.trim()}>
            {saving ? 'Adding…' : '+ Add'}
          </Button>
        </form>
      </Card>

      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : networks.length === 0 ? (
        <EmptyState
          icon={<Network className="h-8 w-8" />}
          title="No network ranges configured yet"
          description='Until you add one, idefy ssh config install fails with a clear error for every user in this tenant.'
        />
      ) : (
        <DataTable>
          <THead>
            <Tr>
              <Th>Network</Th>
              <Th className="text-right">Actions</Th>
            </Tr>
          </THead>
          <tbody>
            {networks.map((n) => (
              <Tr key={n}>
                <Td className="font-mono text-sm">{n}</Td>
                <Td className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-danger"
                    onClick={() => handleDelete(n)}
                  >
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  )
}
